import { useState } from "react";
import { FiPlus, FiZap, FiCheck } from "react-icons/fi";
import Card from "../common/Card";
import Button from "../common/Button";
import DataTable from "../common/DataTable";
import ConfirmDialog from "../common/ConfirmDialog";

const norm = (text) => String(text || "").trim().toLowerCase();

export default function RoleSuggestionsCard({ suggestion, goals = [], editable, onUse, onAddAll }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!editable || !suggestion?.goals?.length) return null;

  const onSheet = new Set(goals.map((g) => norm(g.title)));
  const missing = suggestion.goals.filter((s) => !onSheet.has(norm(s.title)));
  const currentTotal = goals.reduce((sum, g) => sum + (Number(g.weightage) || 0), 0);
  const afterTotal = Math.round((currentTotal + missing.reduce((sum, s) => sum + Number(s.weightage || 0), 0)) * 100) / 100;

  const addAll = async () => {
    setBusy(true);
    const ok = await onAddAll(missing);
    setBusy(false);
    if (ok) setConfirm(false);
  };

  return (
    <Card
      title={`Suggested for your role: ${suggestion.designationName}`}
      subtitle="From the role template HR maintains for your designation. Use them as a starting point and edit anything that does not fit."
      actions={
        missing.length > 0 && (
          <Button size="sm" icon={FiZap} onClick={() => setConfirm(true)}>
            Add all ({missing.length})
          </Button>
        )
      }
      padded={false}
    >
      <DataTable
        dense
        label="Suggested goals"
        rows={suggestion.goals}
        rowKey="key"
        columns={[
          {
            key: "title",
            label: "Suggested goal",
            render: (r) => (
              <span className="cell-stack">
                <strong>{r.title}</strong>
                <small>{r.description}</small>
              </span>
            ),
          },
          { key: "weightage", label: "Goal %", align: "right", render: (r) => `${r.weightage}%` },
          { key: "target", label: "Target", align: "right", render: (r) => `${r.target}${r.unit === "%" ? "%" : ` ${r.unit}`}` },
          {
            key: "actions",
            label: "",
            align: "right",
            render: (r) =>
              onSheet.has(norm(r.title)) ? (
                <span className="tag tag-info"><FiCheck size={12} aria-hidden="true" /> On your sheet</span>
              ) : (
                <Button variant="secondary" size="sm" icon={FiPlus} onClick={() => onUse(r)} aria-label={`Use ${r.title}`}>
                  Use
                </Button>
              ),
          },
        ]}
      />

      <ConfirmDialog
        open={confirm}
        title={`Add ${missing.length} suggested goal${missing.length === 1 ? "" : "s"}?`}
        message={
          afterTotal === 100
            ? `They will be added as drafts with the template weights, bringing your total to exactly 100%.`
            : `They will be added as drafts with the template weights. Your total will be ${afterTotal}%, so adjust the weights to reach exactly 100% before you submit.`
        }
        confirmLabel="Add goals"
        loading={busy}
        onConfirm={addAll}
        onClose={() => setConfirm(false)}
      />
    </Card>
  );
}
